import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lock, Compass, PlayCircle } from "lucide-react";

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="mt-3" data-testid="class-progress">
      <div className="flex items-center justify-between text-[11px] mb-1.5">
        <span className="text-zinc-500 font-mono">
          {done}/{total} chapters practised
        </span>
        <span className="text-yellow-300 font-mono">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function NotStartedCta() {
  return (
    <div
      className="mt-3 flex items-center gap-1.5 text-[11px] text-pink-300 font-medium"
      data-testid="class-not-started"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-pink-300/70" />
      Not started yet — begin your first chapter
    </div>
  );
}

export default function Classes() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [progress, setProgress] = useState(null); // { class_summary, recent, best_per_chapter }

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data));
  }, []);

  useEffect(() => {
    if (!user) {
      setProgress(null);
      return;
    }
    api
      .get("/practice-attempts/mine")
      .then((r) => setProgress(r.data))
      .catch(() => setProgress(null));
  }, [user]);

  // Build per-class practised count from summary
  const practisedByClass = {};
  (progress?.class_summary || []).forEach((s) => {
    practisedByClass[s.class_id] = s.chapters_practised || 0;
  });

  // "Continue where you left off" — most recent practice attempt
  const lastAttempt = progress?.recent?.[0] || null;

  return (
    <div>
      <div className="mb-6 fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Curriculum</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Pick your class</h1>
          <p className="text-zinc-400 mt-2 max-w-xl">
            Complete NCERT coverage for Classes 6 to 10. Class 11-12 live on <Link to="/learn" className="text-yellow-300 underline hover:text-yellow-200">Learn</Link>.
          </p>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.2em] text-pink-300 border border-pink-300/30 rounded-full px-3 py-1"
          data-testid="page-cbse-badge"
        >
          CBSE · NCERT
        </div>
      </div>

      {/* Continue where you left off */}
      {user && lastAttempt && (
        <Link
          to={`/chapters/${lastAttempt.chapter_id}`}
          data-testid="continue-banner"
          className="fade-up mb-5 flex items-center gap-4 rounded-lg border border-yellow-300/25 bg-gradient-to-r from-yellow-300/5 via-orange-300/5 to-pink-300/5 p-4 hover:border-yellow-300/50 transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-yellow-300/10 grid place-items-center shrink-0">
            <PlayCircle className="w-5 h-5 text-yellow-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-300 mb-0.5">Continue where you left off</div>
            <div className="text-sm text-zinc-200 truncate">
              {lastAttempt.class_label} · {lastAttempt.chapter_title || `Chapter ${lastAttempt.chapter_number}`}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-yellow-300 shrink-0" />
        </Link>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="classes-grid">
        {classes.map((c, i) => {
          const done = practisedByClass[c.id] || 0;
          const total = c.chapter_count;
          return (
            <div
              key={c.id}
              data-testid={`class-card-${c.id}`}
              className={`fade-up fade-up-${(i % 4) + 1} lift rounded-lg border p-6 relative overflow-hidden flex flex-col ${
                c.status === "active"
                  ? "border-white/10 bg-[#152040]"
                  : "border-white/5 bg-[#0f1a34] opacity-60"
              }`}
            >
              <div className="font-display text-3xl font-bold mb-2">{c.label}</div>
              <div className="text-sm text-zinc-400 mb-3 min-h-[20px]">{c.tagline}</div>

              {user && c.status === "active" && (done > 0 ? <ProgressBar done={done} total={total} /> : <NotStartedCta />)}

              <div className="flex items-center justify-between mt-auto pt-4">
                <div className="text-xs text-zinc-500 font-mono">
                  {c.chapter_count > 0 ? `${c.chapter_count} chapters` : "Coming soon"}
                </div>
                {c.status === "active" ? (
                  <Link
                    to={`/classes/${c.id}`}
                    className="text-yellow-300 flex items-center gap-1 text-sm font-semibold hover:gap-2 transition-all"
                    data-testid={`class-open-${c.id}`}
                  >
                    Open <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <Lock className="w-3.5 h-3.5" /> Locked
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Filler card — completes the 3×2 grid balance */}
        <Link
          to="/quiz"
          data-testid="placement-card"
          className={`fade-up fade-up-2 lift rounded-lg border border-dashed border-pink-300/30 bg-gradient-to-br from-pink-300/5 via-orange-300/5 to-yellow-300/5 p-6 flex flex-col items-start justify-between hover:border-pink-300/60 transition-colors`}
        >
          <div>
            <div className="w-10 h-10 rounded-full bg-pink-300/10 grid place-items-center mb-3">
              <Compass className="w-5 h-5 text-pink-300" />
            </div>
            <div className="font-display text-xl font-bold mb-2 text-zinc-100">Not sure which class?</div>
            <div className="text-sm text-zinc-400">Take a quick placement quiz and we'll suggest where to start.</div>
          </div>
          <div className="mt-6 flex items-center gap-1 text-sm font-semibold text-pink-300 hover:gap-2 transition-all">
            Start placement quiz <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
      </div>
    </div>
  );
}
